import { Info, Lock, Zap } from 'lucide-react-native';
import { useState } from 'react';
import {
  Pressable,
  StyleSheet,
  Text as RNText,
  View,
  type StyleProp,
  type ViewStyle,
} from 'react-native';

import { Pill } from '@/components/Pill';
import { Text } from '@/components/Text';
import { colors, fonts, fontSizes, radii, spacing } from '@/constants/theme';
import type { Deal } from '@/types/deal';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Format the cap-reset ISO ("2026-08-01T00:00:00-04:00") as "Aug 1" using the
// date parts AS SENT (the server's America/New_York day) — never `new Date()`,
// which would re-interpret in the device tz and can roll to the wrong day.
function formatResetDate(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return '';
  return `${MONTHS[m - 1]} ${d}`;
}

type Tone = 'light' | 'dark';
// Server-owned availability (Deal#availability_status). The "almost gone"
// threshold lives on the server so every surface agrees; the client just maps
// this to color/pill. 'plenty' = comfortable, 'low' = almost gone, 'full' = out.
type Status = 'plenty' | 'low' | 'full';

interface AvailabilityBarProps {
  cap: number;
  spotsLeft: number;
  status: Status;
  resetsAt: string | null;
  tone: Tone;
  /** List-row variant: tighter, no interactive explainer (avoids nested press). */
  compact: boolean;
  style?: StyleProp<ViewStyle>;
}

function AvailabilityBar({
  cap,
  spotsLeft,
  status,
  resetsAt,
  tone,
  compact,
  style,
}: AvailabilityBarProps) {
  const [showHelp, setShowHelp] = useState(false);

  const onDark = tone === 'dark';
  const full = status === 'full';

  // Fill = fraction of spots still LEFT (the server floors spots_left at 0 on
  // overshoot, and cap > 0 whenever this renders). The bar depletes toward empty
  // as the month fills up: lots left ⇒ green, none left ⇒ 0% ⇒ all gray track.
  const fillPct = Math.max(0, Math.min(100, Math.round((spotsLeft / cap) * 100)));

  const fillColor =
    status === 'full' ? colors.trackMutedFill : status === 'low' ? colors.warning : colors.electricGreen;
  // Count reads as a quiet caption (same muted tone as its label) — the bar is
  // the primary signal, the number just annotates it.
  const labelColor = onDark ? colors.textOnDarkMuted : colors.textMuted;
  const resetDate = resetsAt ? formatResetDate(resetsAt) : '';

  const a11y = full
    ? `Full this month${resetDate ? `, reopens ${resetDate}` : ''}`
    : `${spotsLeft} of ${cap} spots left this month`;

  return (
    <View style={[styles.container, style]}>
      <View style={styles.metaRow}>
        {/* Label lives on the count text (not the container): a bare View
            isn't an accessibility element, so a container label is dropped by
            VoiceOver, which then reads the children piecemeal. */}
        <RNText
          style={[styles.count, { color: labelColor }]}
          accessibilityLabel={a11y}
        >
          {`${spotsLeft}`}
        </RNText>
        {/* RN Text, same style as the count: the count already speaks the
            whole sentence, and without hiding this VoiceOver reads "396 of
            400 spots left this month" and then "of 400 left this month"
            again. The app's Text wrapper doesn't forward the hide props. */}
        <RNText
          numberOfLines={1}
          style={[styles.count, styles.subLabel, { color: labelColor }]}
          accessibilityElementsHidden
          importantForAccessibility="no-hide-descendants"
        >
          {full ? (resetDate ? `left · reopens ${resetDate}` : 'left · reopens on the 1st') : `of ${cap} left this month`}
        </RNText>

        {!compact && (
          <Pressable
            onPress={() => setShowHelp((v) => !v)}
            hitSlop={8}
            accessibilityRole="button"
            accessibilityLabel="What does this mean?"
            style={styles.infoBtn}
          >
            <Info size={15} color={labelColor} />
          </Pressable>
        )}

        <View style={styles.badgeSlot}>
          {status === 'low' && (
            <Pill tone="warn" icon={Zap}>
              Almost gone
            </Pill>
          )}
          {status === 'full' && (
            // On the Deep Green hero the light outline tone is Deep Green
            // text on Deep Green: an empty pill (sim, 2026-09-18).
            <Pill tone={onDark ? 'outlineOnDark' : 'outline'} icon={Lock}>
              Full
            </Pill>
          )}
        </View>
      </View>

      <View style={[styles.track, onDark ? styles.trackOnDark : styles.trackOnLight]}>
        <View
          testID="availability-fill"
          style={[styles.fill, { width: `${fillPct}%`, backgroundColor: fillColor }]}
        />
      </View>

      {showHelp && !compact && (
        <View style={[styles.help, onDark ? styles.helpOnDark : styles.helpOnLight]}>
          <Text variant="captionBold" color={onDark ? 'frost' : 'deepGreen'}>
            What does “{spotsLeft} of {cap} left” mean?
          </Text>
          <Text variant="caption" color={onDark ? 'textOnDarkMuted' : 'textMuted'} style={styles.helpBody}>
            Each month only so many people can get paid for this deal. This shows how many spots are
            left — when it hits zero it reopens {resetDate || 'on the 1st'}.
          </Text>
        </View>
      )}
    </View>
  );
}

/**
 * Uncapped deal: the same footprint as the capped bar, so a list of deal
 * cards stays one height whether or not each has a cap, with the one true
 * statement about it. The track is full because there is no ceiling to
 * deplete. (Before this, an uncapped Fetch card rendered nothing here and
 * sat visibly shorter than its SoFi siblings.)
 */
function UncappedBar({ tone, style }: { tone: Tone; style?: StyleProp<ViewStyle> }) {
  const onDark = tone === 'dark';
  return (
    <View style={[styles.container, style]}>
      <View style={styles.metaRow}>
        <Text variant="caption" color={onDark ? 'textOnDarkMuted' : 'textMuted'}>
          No monthly limit
        </Text>
      </View>
      <View
        testID="availability-track"
        style={[styles.track, onDark ? styles.trackOnDark : styles.trackOnLight]}
      >
        <View testID="availability-fill" style={[styles.fill, styles.fillUncapped]} />
      </View>
    </View>
  );
}

/**
 * Per-deal monthly availability ("112 of 120 left this month") with a progress
 * track, or a "No monthly limit" line of the same footprint for an uncapped
 * deal (`monthly_referral_cap == null`), so callers can drop it in
 * unconditionally. Server is the source of truth — this only displays `deal`'s
 * cap fields (including the semantic `availability_status`), it never
 * recomputes the thresholds.
 */
export function DealAvailabilityBar({
  deal,
  tone = 'light',
  compact = false,
  style,
}: {
  deal: Deal;
  tone?: Tone;
  compact?: boolean;
  style?: StyleProp<ViewStyle>;
}) {
  // `monthly_referral_cap` is null exactly when uncapped, same as
  // `availability_status`; gating on the cap keeps the bar right even if an
  // older backend hasn't shipped the status field yet.
  const cap = deal.monthly_referral_cap;
  if (cap == null) return <UncappedBar tone={tone} style={style} />;

  // Server owns the "almost gone" threshold via availability_status. The
  // fallback only covers the backend-rollout gap (older API without the field):
  // derive just full-vs-plenty from spots_left — never the 'low' threshold,
  // which stays server-side on purpose.
  const status: Status = deal.availability_status ?? ((deal.spots_left ?? 0) <= 0 ? 'full' : 'plenty');

  return (
    <AvailabilityBar
      cap={cap}
      spotsLeft={deal.spots_left ?? 0}
      status={status}
      resetsAt={deal.cap_resets_at}
      tone={tone}
      compact={compact}
      style={style}
    />
  );
}

const styles = StyleSheet.create({
  container: {
    gap: spacing.sm,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  // Same font/size as the caption label so the whole line reads as one
  // uniform thin (Inter) caption — no display-font number mixed in.
  count: {
    fontFamily: fonts.body,
    fontSize: fontSizes.sm,
    lineHeight: 18,
  },
  subLabel: {
    flexShrink: 1,
  },
  infoBtn: {
    marginLeft: spacing.xs / 2,
  },
  badgeSlot: {
    marginLeft: 'auto',
  },
  track: {
    height: 7,
    borderRadius: radii.full,
    overflow: 'hidden',
  },
  trackOnLight: {
    backgroundColor: colors.track,
  },
  trackOnDark: {
    backgroundColor: colors.frostTint,
  },
  fill: {
    height: '100%',
    borderRadius: radii.full,
  },
  // Full and green: there is no ceiling to deplete.
  fillUncapped: {
    width: '100%',
    backgroundColor: colors.electricGreen,
  },
  help: {
    borderRadius: radii.base,
    padding: spacing.md,
    gap: spacing.xs / 2,
  },
  helpOnLight: {
    backgroundColor: colors.background,
    borderWidth: 1,
    borderColor: colors.border,
  },
  helpOnDark: {
    backgroundColor: colors.glassFrostFill,
    borderWidth: 1,
    borderColor: colors.glassFrostBorder,
  },
  helpBody: {
    marginTop: spacing.xs / 2,
  },
});
